import type { Patient, Reference } from '@medplum/fhirtypes'
import { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, type ReactNode, useContext } from 'react'
import { ObservationFrameEngagement } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import {
  firstLineOf,
  observationDate,
  shownValue,
} from '#src/components/rich-text-editor/ObservationNode/observationLastValue.ts'
import { ObservationNodeConfigure } from '#src/components/rich-text-editor/ObservationNode/observationNodeConfig.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { captureRecordFor } from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import type {
  ObservationCaptureTarget,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useGroupedObservationHistory } from '#src/hooks/useObservationHistory.ts'

export interface ObservationLatestValueProps {
  attributes: ObservationNodeAttributes
  capture: ObservationCaptureTarget
}

// A node frozen to the latest value: no answer to give, only the
// participant's most recent record for the code, as of now. Its actions
// offer that record's details and history, and going back to capturing.
export function ObservationLatestValue({
  attributes,
  capture,
}: ObservationLatestValueProps): JSX.Element {
  // A template has no participant to read from: the value is resolved in
  // each note made from it.
  const recordFor = capture.template ? undefined : captureRecordFor(capture)
  // The latest capture: every value of a multi-select answer.
  const [latestCapture] = useGroupedObservationHistory({
    participant: recordFor ?? undefined,
    coding: { system: attributes.system, code: attributes.code },
    limit: 1,
  })
  const latest = latestCapture?.members[0]
  const values =
    latestCapture && latestCapture.members.length > 1
      ? latestCapture.values.join(', ')
      : undefined
  const configure = useContext(ObservationNodeConfigure)
  const label = ExtendedObservation.getNickname(
    attributes.code,
    attributes.codingDisplay || attributes.display
  )

  return (
    <ObservationNodeFrame
      // The record shown is the one its details open on; the node's own
      // reference, kept for going back, is not acted on here.
      attributes={{
        ...attributes,
        reference: latest?.reference.reference ?? null,
      }}
      label={label}
      noHover
      valueInDetails={false}
      participant={recordFor ?? undefined}
      // A template has no value to open; the line opens its configuration,
      // as any other question's does.
      onClick={capture.template ? (configure ?? undefined) : undefined}
    >
      {capture.template ? (
        <LatestValue recordFor={undefined} latest={undefined} label={label} />
      ) : (
        <FrozenValue>
          <LatestValue
            recordFor={recordFor}
            latest={latest}
            label={label}
            values={values}
          />
        </FrozenValue>
      )}
    </ObservationNodeFrame>
  )
}

function LatestValue({
  recordFor,
  latest,
  label,
  values,
}: {
  recordFor: Reference<Patient> | null | undefined
  latest: ExtendedObservation | undefined
  label: string
  // A multi-select answer's values, together.
  values?: string
}): JSX.Element {
  if (recordFor === null) {
    return <span className="text-muted-foreground">Choose a participant</span>
  }
  // No participant in scope at all, as in a template: resolved where the
  // note is written, like a registry variable.
  if (recordFor === undefined) {
    return (
      <span
        className="inline-flex h-5 items-center rounded-sm border border-dashed border-gray-450 px-2 text-sm leading-none text-muted-foreground"
        title={`Latest recorded ${label}`}
      >
        Latest
      </span>
    )
  }
  if (!latest) {
    return <span className="text-muted-foreground">No value recorded</span>
  }
  const value =
    values ??
    (latest.valueString ? firstLineOf(latest.valueString) : shownValue(latest))
  const date = observationDate(latest)
  return (
    <>
      <span
        title={`Latest recorded value${date ? ` · ${date}` : ''}`}
        className="min-w-0 truncate rounded-xs bg-muted/60 px-0.5 underline decoration-dotted underline-offset-2"
      >
        {value ?? 'No value recorded'}
      </span>
      {date && (
        <span className="self-center text-xs whitespace-nowrap text-muted-foreground">
          &nbsp;· {date}
        </span>
      )}
    </>
  )
}

// The frozen value is the node's one control, so a click on the label or Tab
// lands on it, engaging the node and showing its actions (History, Details,
// Make answerable); pressing it opens History. Its tint, dotted underline,
// date and tooltip are what mark it frozen.
function FrozenValue({ children }: { children: ReactNode }): JSX.Element {
  const { openHistory } = useContext(ObservationFrameEngagement)
  return (
    <button
      type="button"
      onClick={() => openHistory?.()}
      className="inline-flex min-w-0 items-start rounded-xs text-left focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
    >
      {children}
    </button>
  )
}
